import { ADR, ADR_EDIT, ADR_REVIEW, CEM4601 } from "../_constants/actionConstants";
import axios from "axios";
import moment from "moment";
import _ from "lodash";
import { BASE_URL } from "../_constants/urlConstants";
import { DOCTYPE } from "../_constants/docTypeConstants";
import raiseError from "./raiseError";
import {
  LocationWorkPerformedFactory,
  AdrFactory,
} from "../_reducers/helpers/adrFactories";
import mainTabActions from "./mainTabActions";
import { stringifyJSON } from "../_utils/generalUtils";
import { contextTypes } from "../_constants/fileUploadContextTypes";
import qs from "qs";
import pdfActions from "./pdfActions";
import cem4601PdfActions from "./cem4601PdfActions";
import raiseWarning from "./raiseWarning";

const _msgInvalidArgs = (funcName = "unknown func") =>
  `Invalid argument(s) provided for '${funcName}'. ` +
  "Please contact the system administrator.";

/* **************************************************************************************
 * Notes for implementation:
 * =========================
 * - For simplicity, method names should only be the verb indicating the intended
 *   action. i.e. 'fetch' instead of 'fetchAdr'
 * - After fetching adrDto, do not convert to the client-optimized shape within
 *   the action creator. Rather, dispatch an action that consumes AdrFactory() for
 *   the conversion.
 */

const createV2 = (tabId, authorUserId, projectId) => async (dispatch) => {
  dispatch({ type: ADR.CREATEV2 });
  const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/New/ByUser/${authorUserId}/ForProject/${projectId}`;
  try {
    const res = await axios.get(apiEndpoint);

    // If API returns warning, fallback to client-side create
    const adrDto = res.data.message ? buildBlankAdr(projectId) : res.data;

    dispatch({ type: ADR.CREATEV2_FULFILLED, tabId, adrDto });
  } catch (e) {
    // If API returns error, fallback to client-side create
    const adrDto = buildBlankAdr(projectId);
    dispatch({ type: ADR.CREATEV2_FULFILLED, tabId, adrDto });
  }
};

/**
 * Return a blank adr template (see buildBlankAdr() at the end of this file).
 *
 * @param {number} tabId
 * @param {number} projectId
 * @param {User} user
 * @returns resolve(adrDto)
 */
const create = (tabId, projectId) => (dispatch) =>
  new Promise((resolve, reject) => {
    if (!tabId || !projectId) {
      raiseError(ADR.CREATE_REJECTED, null, _msgInvalidArgs("create"));
      return reject();
    }

    const adrDto = buildBlankAdr(projectId);

    // Calls AdrFactory.initClient()
    dispatch({ type: ADR.CREATE, tabId, adrDto });
    // Sets fetching->false
    dispatch({ type: ADR.CREATE_FULFILLED });

    resolve();
  });

const deleteAdr = (adrId, projectId) => async (dispatch) => {
  dispatch({ type: ADR.DELETE_ADR });
  if (!adrId) {
    raiseError(ADR.DELETE_ADR_REJECTED, null, _msgInvalidArgs("delete"));
    return;
  }

  const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/${adrId}`;
  try {
    const res = await axios.delete(apiEndpoint);
    dispatch({ type: ADR.DELETE_ADR_FULFILLED });
    return;
  } catch (e) {
    return;
  }
};

/**
 * Creates a copy of the provided Adr. For use by actionScripts.COPY_ADR only.
 *
 * @param {string} idCheckoutToken - a UUID
 */
const copy = (tabId, adrId, authorUserId) => async (dispatch, getState) => {
  dispatch({ type: ADR.COPY });
  const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/${adrId}/Copy/ByAuthor/${authorUserId}`;
  try {
    const res = await axios.get(apiEndpoint);
    if (res.data.message) {
      raiseWarning(ADR.AMEND_REJECTED, res.data.message);
      return null;
    }

    const projItemList = getState().bidItems.projItemLists[res.data.projectId] || null;
    const projCcoList = getState().ccos.projCcoLists[res.data.projectId] || null;
    const projPendingIssueList =
      getState().pendingIssues.projPendingIssueLists[res.data.projectId] || null;
    const projLaborList = getState().labors.projLaborLists[res.data.projectId] || null;
    const projEquipList = getState().equipment.projEquipLists[res.data.projectId] || null;

    dispatch({
      type: ADR.COPY_FULFILLED,
      adrDto: res.data,
      tabId,
      projItemList,
      projCcoList,
      projPendingIssueList,
      projLaborList,
      projEquipList,
    });
    return res.data.assistantDailyReportId;
  } catch (e) {
    console.error(e);
    raiseError(ADR.COPY_REJECTED, e.response.data);
  }
};

const copyOnClient = (tabIdOfCopy, tabIdOfOriginal) => (dispatch) =>
  new Promise((resolve) => {
    // Calls copyClient(), places copied adr in new tab
    dispatch({ type: ADR.COPY_ON_CLIENT, tabIdOfOriginal, tabIdOfCopy });
    // Sets fetching->false
    dispatch({ type: ADR.COPY_ON_CLIENT_FULFILLED });

    resolve();
  });

/**
 * Fetches a copy of Adr of the provided Id, for amendment. The Adr being
 * amended is not yet marked superseded until this amendment is approved.
 *
 * @param {string} idCheckoutToken
 * @param {number} tabId
 * @param {number} adrIdToAmend
 * @param {number} amendedByUserId
 */
const amend =
  (idCheckoutToken = null, tabId = null, adrIdToAmend, amendedByUserId) =>
  async (dispatch, getState) => {
    dispatch({ type: ADR.AMEND });
    const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/Amend/${adrIdToAmend}/ByUser/${amendedByUserId}`;
    try {
      const adrRes = await axios.get(apiEndpoint);
      if (adrRes.data.message) {
        raiseWarning(ADR.AMEND_REJECTED, adrRes.data.message);
        return null;
      }
      if (idCheckoutToken)
        // If token provided, get tabId
        tabId = getState().mainTabs.reservedTabIds[idCheckoutToken];
      if (tabId) {
        const projItemList =
          getState().bidItems.projItemLists[adrRes.data.projectId] || null;
        const projCcoList = getState().ccos.projCcoLists[adrRes.data.projectId] || null;
        const projPendingIssueList =
          getState().pendingIssues.projPendingIssueLists[adrRes.data.projectId] || null;
        const projLaborList =
          getState().labors.projLaborLists[adrRes.data.projectId] || null;
        const projEquipList =
          getState().equipment.projEquipLists[adrRes.data.projectId] || null;

        dispatch({
          type: ADR.AMEND_FULFILLED,
          tabId,
          adrDto: adrRes.data,
          projItemList,
          projCcoList,
          projPendingIssueList,
          projLaborList,
          projEquipList,
        });
        return adrRes.data.assistantDailyReportId;
      }
    } catch (err) {
      raiseError(ADR.AMEND_REJECTED, err.response.data);
      return;
    }
  };

/**
 * Fetch the requested ADR. Also fetches feederLists and labor/equipment
 * sets required for ADR. Client must provide EITHER idCheckoutToken OR
 * tabId.
 *
 * @param {?string} idCheckoutToken - UUID token
 * @param {?number} tabId - provided only if re-fetching in-tab
 * @param {number} adrId
 * @returns {function(function())}
 */
const fetch =
  (idCheckoutToken = null, tabId = null, adrId) =>
  async (dispatch, getState) => {
    dispatch({ type: ADR.FETCH_ADR });
    const errType = ADR.FETCH_ADR_REJECTED;
    if (!idCheckoutToken && !tabId) {
      raiseError(errType, null, "idCheckoutToken or tabId must be provided. (fetchAdr)");
      return;
    }

    const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/${adrId}`;
    try {
      const res = await axios.get(apiEndpoint);

      if (idCheckoutToken)
        // If token provided, get tabId
        tabId = getState().mainTabs.reservedTabIds[idCheckoutToken];
      if (tabId) {
        const projItemList =
          getState().bidItems.projItemLists[res.data.projectId] || null;
        const projCcoList = getState().ccos.projCcoLists[res.data.projectId] || null;
        const projPendingIssueList =
          getState().pendingIssues.projPendingIssueLists[res.data.projectId] || null;
        const projLaborList =
          getState().labors.projLaborLists[res.data.projectId] || null;
        const projEquipList =
          getState().equipment.projEquipLists[res.data.projectId] || null;

        dispatch({
          type: ADR.FETCH_ADR_FULFILLED,
          tabId,
          adrDto: res.data,
          projItemList,
          projCcoList,
          projPendingIssueList,
          projLaborList,
          projEquipList,
        });
        // Return the adrDto so tab label can be updated with adrDto's reportStartDate.
        // (This is the only reason for returning adrDto - it is already converted to the
        // client-optimized shape and saved into redux store in FETCH_ADR_FULFILLED.)
        return res.data;
      }
    } catch (err) {
      raiseError(errType, err.response.data);
      return;
    }
  };

/**
 * Fetch list of ADR's for the specified project.
 *
 * @param {number} projectId
 * @param {number} tabId
 * @param {Date} includeFrom - filter selection, a moment()
 */
const fetchAll =
  (projectId, tabId, includeFrom = null) =>
  (dispatch) =>
    new Promise((resolve, reject) => {
      dispatch({ type: ADR.FETCH_ADR_LIST });
      const errType = ADR.FETCH_ADR_LIST_REJECTED;
      const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/ListForProject/${projectId}`;

      if (!projectId || !tabId) {
        const msg =
          "Invalid parameter(s) provided: projectId and tabId must be nonzero numbers.";
        raiseError(errType, null, msg);
        return reject();
      }

      // If includeFrom is null, set it to the most recent option
      // (= last month's first day)
      if (!includeFrom)
        includeFrom = moment().subtract(1, "months").startOf("month").format();

      axios
        .get(apiEndpoint, { params: { includeFrom } })
        .then((response) => {
          dispatch({
            type: ADR.FETCH_ADR_LIST_FULFILLED,
            adrList: response.data,
            tabId,
            projectId,
            includeFrom,
          });
          resolve();
        })
        .catch((err) => {
          raiseError(errType, err);
          return reject();
        });
    });

/**
 * Finds any open adrList tab and updates its adrList based on
 * the list's "includeFrom" filter.
 *
 * Does not include catch block for errors - defer error handling to
 * client action handlers.
 *
 * @param {number} projectId
 */
const updateAdrList = (projectId) => (dispatch, getState) =>
  new Promise((resolve) => {
    dispatch({ type: ADR.UPDATE_ADR_LIST });
    const openAdrListTab = getState().mainTabs.openTabs.find(
      (t) => t.projectId === projectId && t.docType === DOCTYPE.ADR_LIST
    );
    if (openAdrListTab) {
      const openAdrList = getState().adr.openAdrLists[openAdrListTab.tabId] || null;
      if (openAdrList) {
        dispatch(fetchAll(projectId, openAdrListTab.tabId, openAdrList.includeFrom)).then(
          () => resolve()
        );
      }
    }
  });

const markAsSigned = (tabId) => async (dispatch, getState) => {
  dispatch({ type: ADR.GENERIC_REQUEST });

  const adr = getState().adr.openAdrs[tabId];
  if (!adr) {
    raiseError(ADR.GENERIC_REQUEST_REJECTED, null, "Could not find assisstant daily report by tabId.");
    return;
  }

  const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/${adr.assistantDailyReportId}/SignOnSubmit`;
  try {
    const res = await axios.patch(apiEndpoint);
    dispatch({ type: ADR.GENERIC_REQUEST_FULFILLED });
    return;
  } catch (e) {
    raiseError(ADR.GENERIC_REQUEST_REJECTED, e.response.error);
  }
};

const submit = (tabId) => async (dispatch, getState) => {
  dispatch({ type: ADR.SUBMIT });

  const adr = getState().adr.openAdrs[tabId];
  if (!adr) {
    raiseError(ADR.SUBMIT_REJECTED, null, "Could not find daily report by tabId.");
    return;
  }

  // adrId could be 0, it's ok to send as is. Backend will handle creation.
  const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/${adr.assistantDailyReportId}/Submit`;

  // We are using AdrFactory directly within this action creator, instead of
  // dispatching an action to be handled by the reducer, since we just want
  // a conversion from client-optimized adr to adrDto. For this task,
  // dispatching an action would not result in any changes to the redux store,
  // which would be an antipattern.
  const adrFactory = new AdrFactory(adr);
  const adrDto = adrFactory.mapClientToDto();

  try {
    const res = await axios.put(apiEndpoint, adrDto);
    if (res.data.message) {
      raiseWarning(ADR.SUBMIT_REJECTED, res.data.message);
      return false;
    } else {
      dispatch({
        type: ADR.SUBMIT_FULFILLED,
        adrId: res.data,
        tabId,
      });

      dispatch(mainTabActions.unmarkTabContentDirty(tabId));

      // Return new adrId if new adr
      return res.data;
    }
  } catch (err) {
    raiseError(ADR.SUBMIT_REJECTED, err.response.data);
    return false;
  }
};

const save = (tabId) => async (dispatch, getState) => {
  dispatch({ type: ADR.SAVE_ADR });

  const errType = ADR.SAVE_ADR_REJECTED;

  const adr = getState().adr.openAdrs[tabId];
  if (!adr) {
    raiseError(errType, null, "Could not find daily report by tabId.");
    return;
  }

  const apiEndpoint =
    adr.assistantDailyReportId === 0
      ? `${BASE_URL}/api/AssistantDailyReports/CreateFromPayload` // POST
      : `${BASE_URL}/api/AssistantDailyReports/${adr.assistantDailyReportId}/UpdateDraft`; // PUT

  // We are using AdrFactory directly within this action creator, instead of
  // dispatching an action to be handled by the reducer, since we just want
  // a conversion from client-optimized adr to adrDto. For this task,
  // dispatching an action would not result in any changes to the redux store,
  // which would be an antipattern.
  const adrFactory = new AdrFactory(adr);
  const adrDto = adrFactory.mapClientToDto();

  try {
    const res =
      adr.assistantDailyReportId === 0
        ? await axios.post(apiEndpoint, adrDto)
        : await axios.put(apiEndpoint, adrDto);
    if (res.data.message) {
      raiseWarning(errType, res.data.message);
      return false;
    } else {
      dispatch({
        type: ADR.SAVE_ADR_FULFILLED,
        adrId: res.data,
        tabId,
      });

      dispatch(mainTabActions.unmarkTabContentDirty(tabId));

      return res.data;
    }
  } catch (err) {
    raiseError(errType, err.response.data);
    return false;
  }
};

const fetchNextToReview = (tabId) => async (dispatch, getState) => {
  dispatch({ type: ADR.GET_NEXT_TO_REVIEW });

  const adr = getState().adr.openAdrs[tabId];
  const errType = ADR.GET_NEXT_TO_REVIEW_REJECTED;

  if (!adr) {
    raiseError(errType, null, "Could not find daily report by tabId.");
    return;
  }

  const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/NextSubmittedForProject/${adr.projectId}`;
  const params = {
    dateFrom: moment(adr.reportStartDate).format(),
  };
  try {
    const res = await axios.get(apiEndpoint, { params });
    // response.data is adrId of the next Adr to review.
    // If none found for review, response.data is null
    dispatch({ type: ADR.GET_NEXT_TO_REVIEW_FULFILLED });
    return res.data;
  } catch (err) {
    raiseError(errType, err.response.data);
    return;
  }
};

/**
 * Fetch amendment history before AND after the provided Adr. History is a kvp,
 * where key: adrId, and value: approvedOn timestamp
 * @param {Number} adrId
 */
const fetchAmendmentHistory = (adrId) => async (dispatch) => {
  dispatch({ type: ADR.FETCH_AMEND_HISTORY });
  const apiEndpoint =
    BASE_URL + "/api/AssistantDailyReports/GetAmendmentHistory/?adrId=" + adrId;
  try {
    const res = await axios.get(apiEndpoint);
    const validatedHist =
      Array.isArray(res.data.amendmentHistory) && res.data.amendmentHistory.length > 0
        ? res.data.amendmentHistory
        : null;
    dispatch({
      type: ADR.FETCH_AMEND_HISTORY_FULFILLED,
      originalAdrId: res.data.originalAssistantDailyReportId,
      amendmentHistory: validatedHist,
    });
    return;
  } catch (err) {
    raiseError(ADR.FETCH_AMEND_HISTORY_REJECTED, err.response.data);
    return;
  }
};

const search =
  (searchParams, idCheckoutToken = null, tabId = null) =>
  (dispatch, getState) =>
    new Promise((resolve, reject) => {
      dispatch({ type: ADR.FETCH_ADR_SEARCH });
      const errType = ADR.FETCH_ADR_SEARCH_REJECTED;
      const apiEndpoint = BASE_URL + "/api/AssistantDailyReports/SearchAdr";
      axios
        .post(apiEndpoint, searchParams)
        .then((response) => {
          if (idCheckoutToken)
            tabId = getState().mainTabs.reservedTabIds[idCheckoutToken];
          if (tabId) {
            dispatch({
              type: ADR.FETCH_ADR_SEARCH_FULFILLED,
              adrList: response.data,
              searchParams,
              tabId,
            });
            resolve();
          }
        })
        .catch((err) => {
          raiseError(errType, err.response.data);
          return reject();
        });
    });

const retractSubmission = (adrId) => async (dispatch) => {
  dispatch({ type: ADR.RETRACT_SUBMIT });
  const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/${adrId}/RetractSubmit`;
  try {
    await axios.patch(apiEndpoint);
    dispatch({ type: ADR.RETRACT_SUBMIT_FULFILLED });
  } catch (e) {
    dispatch({ type: ADR.RETRACT_SUBMIT_REJECTED });
    raiseError(ADR_REVIEW.START_REJECTED, err.response.data);
  }
};

const startReview = (tabId, adrId, reviewerUserId) => async (dispatch) => {
  dispatch({ type: ADR_REVIEW.START });
  const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/${adrId}/Review/Start/${reviewerUserId}`;
  try {
    const res = await axios.patch(apiEndpoint);
    if (res.data.message) {
      raiseWarning(ADR_REVIEW.START_REJECTED, res.data.message);
      return;
    }

    // This dispatch for ADR_REVIEW.START_FULFILLED is only for the current user, therefore
    // 'revokeReview' is always false. Revokation of review will occur via SignalR
    // "PatchRequest" to all OTHER reviewers who may have this DR currently open.
    dispatch({
      type: ADR_REVIEW.START_FULFILLED,
      tabId,
      reviewerUserId,
      reviewStartedOn: res.data.reviewStartedOn,
      revokeReview: false,
    });
    return;
  } catch (e) {
    dispatch({ type: ADR_REVIEW.START_REJECTED });
    raiseError(ADR_REVIEW.START_REJECTED, err.response.data);
  }
};

const reject = (tabId, reviewerUserId) => async (dispatch, getState) => {
  dispatch({ type: ADR_REVIEW.REJECT });

  if (!tabId || !reviewerUserId) {
    dispatch({ type: ADR_REVIEW.REJECT_REJECTED });
    return;
  }

  const adr = getState().adr.openAdrs[tabId];
  if (!adr) {
    raiseError(ADR_REVIEW.REJECT_REJECTED, null, "Could not find daily report by tabId.");
    return;
  }

  // Map only the relevant adr properties into adrReview
  /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportReview} */
  const adrReview = {
    assistantDailyReportId: adr.assistantDailyReportId,
    projectId: adr.projectId,
    authorUserId: adr.authorUserId,
    reviewerUserId,
    reportStartDate: moment(adr.reportStartDate).format(),
    correctionComments: adr.correctionComments.map((coc) => ({
      ...coc,
      commentDate: moment(coc.commentDate).format(),
      fieldInfo: stringifyJSON(coc.fieldInfo),
    })),
    clarificationComments: adr.clarificationComments.map((clc) => ({
      ...clc,
      commentDate: moment(clc.commentDate).format(),
      keywordIds: [...clc.keywordIds],
    })),
  };

  const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/${adr.assistantDailyReportId}/Review/Reject`;
  try {
    const res = await axios.put(apiEndpoint, adrReview);
    if (res.data.message) {
      raiseWarning(ADR_REVIEW.REJECT_REJECTED, res.data.message);
      return;
    }

    dispatch({ type: ADR_REVIEW.REJECT_FULFILLED, tabId, reviewerUserId });
    return;
  } catch (err) {
    raiseError(ADR_REVIEW.REJECT_REJECTED, err.response.data);
    return;
  }
};

const saveReview = (tabId) => async (dispatch, getState) => {
  dispatch({ type: ADR_REVIEW.SAVE });

  if (!tabId) {
    dispatch({ type: ADR_REVIEW.SAVE_REJECTED });
    return;
  }

  const adr = getState().adr.openAdrs[tabId];
  if (!adr) {
    raiseError(ADR_REVIEW.SAVE_REJECTED, null, "Could not find daily report by tabId.");
    return;
  }

  // Map only the relevant adr properties into adrReview
  /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportReview} */
  const adrReview = {
    assistantDailyReportId: adr.assistantDailyReportId,
    projectId: adr.projectId,
    authorUserId: adr.authorUserId,
    reviewerUserId: 0, // Not used when saving review
    reportStartDate: moment(adr.reportStartDate).format(),
    correctionComments: adr.correctionComments.map((coc) => ({
      ...coc,
      commentDate: moment(coc.commentDate).format(),
      fieldInfo: stringifyJSON(coc.fieldInfo),
    })),
    clarificationComments: adr.clarificationComments
      .filter((clc) => clc.subject || clc.comment)
      .map((clc) => ({
        ...clc,
        commentDate: moment(clc.commentDate).format(),
        keywordIds: [...clc.keywordIds],
      })),
  };

  const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/${adr.assistantDailyReportId}/Review/Save`;
  try {
    const res = await axios.put(apiEndpoint, adrReview);
    if (res.data.message) {
      raiseWarning(ADR_REVIEW.SAVE_REJECTED, res.data.message);
      return;
    }

    dispatch({ type: ADR_REVIEW.SAVE_FULFILLED });
    return;
  } catch (err) {
    dispatch({ type: ADR_REVIEW.SAVE_REJECTED });
    raiseError(ADR_REVIEW.SAVE_REJECTED, err.response.data);
    return;
  }
};

const approve = (tabId, reviewerUserId) => async (dispatch, getState) => {
  dispatch({ type: ADR_REVIEW.APPROVE });

  const adr = getState().adr.openAdrs[tabId];
  if (!adr) {
    raiseError(ADR_REVIEW.REJECT_REJECTED, null, "Could not find daily report by tabId.");
    return;
  }

  // Map only the relevant adr properties into adrReview
  /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportReview} */
  const adrReview = {
    assistantDailyReportId: adr.assistantDailyReportId,
    projectId: adr.projectId,
    authorUserId: adr.authorUserId,
    reviewerUserId,
    reportStartDate: moment(adr.reportStartDate).format(),
    correctionComments: [], // Delete all correction comments
    clarificationComments: adr.clarificationComments.map((clc) => ({
      ...clc,
      commentDate: moment(clc.commentDate).format(),
    })),
  };

  const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/${adr.assistantDailyReportId}/Review/Approve`;
  try {
    const res = await axios.put(apiEndpoint, adrReview);
    if (res.data.message) {
      raiseWarning(ADR_REVIEW.APPROVE_REJECTED, res.data.message);
      return;
    }

    dispatch({ type: ADR_REVIEW.APPROVE_FULFILLED, tabId });

    // Create metadata, and request signature if required
    dispatch(cem4601PdfActions.signPdfForApproval(adr.assistantDailyReportId));

    return;
  } catch (err) {
    raiseError(ADR_REVIEW.REJECT_REJECTED, err.response.data);
    return;
  }
};

const fetchCorrectionComments = (adrId) => async (dispatch, getState) => {
  const openAdrs = getState().adr.openAdrs;
  const tabId = Object.keys(openAdrs).find(
    (tabId) => openAdrs[tabId].assistantDailyReportId === adrId
  );

  // If tab for this adr is not open, skip.
  if (!tabId) return;

  const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/${adrId}/CorrectionComments`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({
      type: ADR.FETCH_CORRECTION_COMMENTS_FULFILLED,
      tabId: +tabId,
      correctionComments: res.data,
    });
    return;
  } catch (e) {
    raiseError(ADR.FETCH_CORRECTION_COMMENTS_REJECTED, err.response.data);
    return;
  }
};

const fetchClarificationComments = (adrId) => async (dispatch, getState) => {
  const openAdrs = getState().adr.openAdrs;
  const tabId = Object.keys(openAdrs).find(
    (tabId) => openAdrs[tabId].assistantDailyReportId === adrId
  );

  // If tab for this adr is not open, skip.
  if (!tabId) return;

  const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/${adrId}/ClarificationComments`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({
      type: ADR.FETCH_CLARIFICATION_COMMENTS_FULFILLED,
      tabId: +tabId,
      clarificationComments: res.data,
    });
    return;
  } catch (e) {
    raiseError(ADR.FETCH_CLARIFICATION_COMMENTS_REJECTED, err.response.data);
    return;
  }
};

const uploadAttachmentV2 = (formData) => async (dispatch) => {
  dispatch({ type: ADR.ADR_UPLOAD_ATTACHMENT });
  const errType = ADR.ADR_UPLOAD_ATTACHMENT_REJECTED;
  const apiEndpoint = BASE_URL + "/api/AssistantDailyReports/UploadAttachmentsV2";
  try {
    const res = await axios.post(apiEndpoint, formData);
    dispatch({ type: ADR.ADR_UPLOAD_ATTACHMENT_FULFILLED });
    return res.data;
  } catch (err) {
    raiseError(errType, err.response.data);
    return;
  }
};

const uploadAttachment = (formData) => (dispatch) =>
  new Promise((resolve, reject) => {
    dispatch({ type: ADR.ADR_UPLOAD_ATTACHMENT });
    const errType = ADR.ADR_UPLOAD_ATTACHMENT_REJECTED;
    const apiEndpoint = BASE_URL + "/api/AssistantDailyReports/UploadAttachments";
    axios
      .post(apiEndpoint, formData)
      .then((response) => {
        dispatch({ type: ADR.ADR_UPLOAD_ATTACHMENT_FULFILLED });
        resolve(response.data);
      })
      .catch((err) => {
        const errorData = err.response?.data;
        // Do not raise error, handle in UI as alert message for users
        const isFileSizeError =
          errorData?.type?.includes("BadHttpRequestException") ||
          errorData?.message?.toLowerCase().includes("request body too large");
        if (!isFileSizeError) {
          raiseError(errType, errorData);
        }
        return reject(errorData);
      });
  });

// Temporarily store tempFilePaths until Adr is saved.
// If changes to file attachments are not saved, tempFilePaths
// are then retrieved for file deletion.
const addTempFilePaths = (tabId, filePaths) => (dispatch) => {
  dispatch({
    type: ADR.ADR_ADD_TEMP_FILEPATHS,
    tabId,
    filePaths,
  });
};

const clearTempFilePaths = (tabId) => ({
  type: ADR.ADR_CLEAR_TEMP_FILEPATHS,
  tabId,
});

const toggleDeleteAttachment = (delAttachmentParams, contextType) => (dispatch) =>
  new Promise((resolve, reject) => {
    if (contextType === contextTypes.LOCWP) {
      dispatch({
        type: ADR_EDIT.LOCATION_WORKPERFORMED.ATTACHMENTS.TOGGLE_DELETE,
        ...delAttachmentParams,
      });
    } else if (contextType === contextTypes.GC) {
      dispatch({
        type: ADR_EDIT.GENERAL_COMMENTS.ATTACHMENTS.TOGGLE_DELETE,
        ...delAttachmentParams,
      });
    }
    resolve();
  });

const deleteAttachments = (filePaths) => (dispatch) =>
  new Promise((resolve, reject) => {
    dispatch({ type: ADR.ADR_DELETE_ATTACHMENT });
    const errType = ADR.ADR_DELETE_ATTACHMENT_REJECTED;
    if (filePaths.length > 0) {
      const apiEndpoint = BASE_URL + "/api/AssistantDailyReports/DeleteAttachments";
      axios
        .post(apiEndpoint, filePaths)
        .then(() => {
          dispatch({ type: ADR.ADR_DELETE_ATTACHMENT_FULFILLED });
          return resolve();
        })
        .catch((err) => {
          raiseError(errType, err.response.data);
          return reject();
        });
    }

    dispatch({ type: ADR.ADR_DELETE_ATTACHMENT_FULFILLED });
    return resolve();
  });

// Delete files attached during current Adr session that are not to be persisted
const deleteTempAttachments = (tabId) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const errType = ADR.ADR_DELETE_ATTACHMENT_REJECTED;
    const filePaths = getState().adr.tempFilePaths[tabId];
    dispatch({ type: ADR.ADR_DELETE_ATTACHMENT });
    if (filePaths && filePaths.length > 0) {
      const apiEndpoint = BASE_URL + "/api/AssistantDailyReports/DeleteAttachments";
      axios
        .post(apiEndpoint, filePaths)
        .then(() => {
          dispatch({
            type: ADR.ADR_DELETE_TEMP_ATTACHMENT_FULFILLED,
            tabId,
          });
          resolve();
        })
        .catch((err) => {
          raiseError(errType, err.response.data);
          return reject();
        });
    } else {
      dispatch({ type: ADR.ADR_DELETE_ATTACHMENT_FULFILLED, tabId });
      resolve();
    }
  });

/**
 * Though it doesn't send any requests to the backend, it still implements
 * a Promise. This is to allow the newWpTabId to be accessible by client -
 * without Promise, newWpTabId somehow is not visible simply through props.
 * @param {number} tabId
 * @param {WorkPerformed} newWp
 */
const addWorkPerformed = (tabId, newWp) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    // Below dispatch adds newWp to the adr in store, and assigns new wpTabId.
    dispatch({ type: ADR_EDIT.WORKPERFORMED.ADD, tabId, newWp });
    const newWpTabId = getState().adr.openAdrs[tabId].newWpTabId || null;
    resolve(newWpTabId);
  });

const deleteWorkPerformed = (tabId, wpTabId) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    dispatch({ type: ADR_EDIT.WORKPERFORMED.DELETE });
    const workPerformedClient = getState().adr.openAdrs[tabId].workPerformedClient;
    const filesToDelete = workPerformedClient[wpTabId].locationWorkPerformed
      .map((locWp) => [
        ...locWp.attachments.map((f) => f.filePath),
        ...locWp.attachments.map((f) => f.imageThumbnailFilePath),
      ])
      .flat();
    dispatch(deleteAttachments(filesToDelete))
      .then(() => {
        dispatch({ type: ADR_EDIT.WORKPERFORMED.DELETE_FULFILLED, tabId, wpTabId });
        resolve();
      })
      .catch(() => dispatch({ type: ADR_EDIT.WORKPERFORMED.DELETE_REJECTED }));
    // Return DELETE_FULFILLED in reject for now, user's original requested action
    // is to delete the workPerformed.
  });

const getWorkPerformedSortOrder = (tabId) => (dispatch, getState) => {
  let adr = getState().adr.openAdrs[tabId];
  if (!adr) return [];

  // workPerformedClient should not be undefined at this point.
  if (!adr.workPerformedClient) return [];

  return _.orderBy(
    Object.entries(adr.workPerformedClient)
      .filter(([key, value]) => key !== "newWpTabId" && typeof value === "object")
      .map(([wpTabId, wp]) => ({
        wpTabId: +wpTabId,
        sortOrder: +wp.sortOrder,
      })),
    "sortOrder",
    "asc"
  );
};

const getLocWorkPerformedSortOrder = (tabId, wpTabId) => (dispatch, getState) => {
  let adr = getState().adr.openAdrs[tabId];
  if (!adr || !adr.workPerformedClient[wpTabId]) return [];

  const locWpIn = adr.workPerformedClient[wpTabId].locationWorkPerformed;
  if (locWpIn.length === 0) return [];

  const locWpm = new LocationWorkPerformedFactory(locWpIn);
  return locWpm.getSortOrder();
};

const fetchPreviewData = (adrId) => async (dispatch) => {
  dispatch({ type: ADR.FETCH_PREVIEW });
  const errType = ADR.FETCH_PREVIEW_REJECTED;
  const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/${adrId}/Preview`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({ type: ADR.FETCH_PREVIEW_FULFILLED });
    return res.data;
  } catch (err) {
    raiseError(errType, err.response.data);
    return;
  }
};

const fetchSummary = (projectId) => async (dispatch) => {
  dispatch({ type: ADR.FETCH_SUMMARY });
  const errType = ADR.FETCH_SUMMARY_REJECTED;
  const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/SummaryForProject/${projectId}`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({ type: ADR.FETCH_SUMMARY_FULFILLED });
    return res.data;
  } catch (err) {
    raiseError(errType, err.response.data);
    return;
  }
};

const getFile = (filePath) => async (dispatch) => {
  dispatch({ type: ADR.FETCH_IMAGE });
  const apiEndpoint = `${BASE_URL}/api/AssistantDailyReports/File/${encodeURIComponent(
    filePath
  )}`;
  try {
    const fileRes = await axios.get(apiEndpoint, { responseType: "blob" });
    const fileName =
      fileRes.headers["content-disposition"].split("filename=")[1].split(";")[0].trim() ||
      "image";
    const mimeType = fileRes.headers["content-type"];
    const imageBlob = new Blob([fileRes.data], { type: mimeType });

    const url = URL.createObjectURL(imageBlob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    dispatch({ type: ADR.FETCH_IMAGE_FULFILLED });
    return;
  } catch (e) {
    console.error(e);
    alert("Could not retrieve the selected file.");
    // raiseError(ADR.FETCH_IMAGE_REJECTED, e);
    return;
  }
};

/**
 * Saves all TA data prior to closing the TA modal.
 * In essence, this is a specialized UPDATE action that updates all editable
 * fields of TA and performs some follow-up actions.
 * @param {number} tabId
 * @param {import("../_typedefs/adrV3TypeDefs").TentativeAgreementClient} ta
 * @param {boolean} applyHoursToDR
 * @returns
 */
const saveTentativeAgreement = (tabId, ta, applyHoursToDR) => (dispatch) => {
  dispatch({
    type: ADR_EDIT.TENTATIVE_AGREEMENTS.UPDATE,
    tabId,
    // Below 'ta.clientId' seems redundant but it's actually not... It eliminates the need
    // for a separate 'clientId' param in this function, while allowing the 'UPDATE' action
    // to be reused where only partial group of fields (that may NOT contain clientId)
    // are to be updated
    clientId: ta.clientId,
    fields: ta,
  });

  if (applyHoursToDR) {
    dispatch({
      type: ADR_EDIT.LABORSET.APPLY_HOURS_FROM_TA,
      tabId,
      tentativeAgreement: ta,
    });
    dispatch({
      type: ADR_EDIT.EQUIPMENTSET.APPLY_HOURS_FROM_TA,
      tabId,
      tentativeAgreement: ta,
    });
    dispatch({ type: ADR_EDIT.LABORSET.HIDE_ALL_OT, tabId });
    dispatch({ type: ADR_EDIT.EQUIPMENTSET.HIDE_ALL_OT, tabId });
  }
};

const tentativeAgreementIsUnique =
  (tabId, workPerformedId, contractorId) => (dispatch, getState) => {
    const adr = getState().adr.openAdrs[tabId] || null;
    if (!adr) return false;

    const isUnique = !(adr.tentativeAgreements || []).some(
      (ta) =>
        ta.workPerformedId === workPerformedId && contractorId in ta.contractorsClient
    );

    return isUnique;
  };

const deleteTentativeAgreementTempFiles =
  (tabId, taClientId) => async (dispatch, getState) => {
    const filePaths =
      tabId in getState().adr.miscSessionData &&
      "tentativeAgreementTempFilePaths" in getState().adr.miscSessionData[tabId] &&
      taClientId in getState().adr.miscSessionData[tabId].tentativeAgreementTempFilePaths
        ? getState().adr.miscSessionData[tabId].tentativeAgreementTempFilePaths[
            taClientId
          ]
        : [];
    dispatch({ type: ADR.ADR_DELETE_ATTACHMENT });
    if (filePaths && filePaths.length > 0) {
      try {
        const apiEndpoint = BASE_URL + "/api/AssistantDailyReports/DeleteAttachments";
        await axios.post(apiEndpoint, filePaths);
        dispatch({
          type: ADR.ADR_DELETE_TEMP_ATTACHMENT_FULFILLED,
          tabId,
        });
      } catch (err) {
        raiseError(errType, err.response.data);
        return;
      }
    } else {
      dispatch({ type: ADR.ADR_DELETE_ATTACHMENT_FULFILLED, tabId });
      return;
    }
  };

const requestAttachments = (fullEA, files) => async (dispatch) => {
  dispatch({ type: ADR.DOWNLOAD_ATTACHMENTS });
  const apiEndpoint = BASE_URL + "/api/AssistantDailyReports/RequestAttachments/";
  const adrFileReq = { fullEA, files };
  try {
    await axios.post(apiEndpoint, adrFileReq);
    dispatch({ type: ADR.DOWNLOAD_ATTACHMENTS_FULFILLED });
    return;
  } catch (err) {
    raiseError(ADR.DOWNLOAD_ATTACHMENTS_REJECTED, err.response.data);
    return;
  }
};

const downloadZipFile = (relFilePath) => async (dispatch) => {
  dispatch({ type: ADR.GENERIC_REQUEST });
  const apiEndpoint = BASE_URL + "/api/AssistantDailyReports/DownloadZipFile/";
  try {
    const res = await axios.get(apiEndpoint, {
      params: { relFilePath },
      paramsSerializer: (params) => qs.stringify(params),
      responseType: "blob",
    });
    if (res.data) {
      const zipFileBlob = new Blob([res.data], { type: "application/zip" });

      const url = URL.createObjectURL(zipFileBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "attachments.zip");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      dispatch({ type: ADR.GENERIC_REQUEST_FULFILLED });
    }
    return;
  } catch (err) {
    raiseError(ADR.GENERIC_REQUEST_REJECTED, err.response.data || err);
    return;
  }
};

// Keep this interface alphabetical
const adrActions = {
  addTempFilePaths,
  addWorkPerformed,
  amend,
  approve,
  clearTempFilePaths,
  copy,
  create,
  createV2,
  deleteAdr,
  deleteAttachments,
  deleteTentativeAgreementTempFiles,
  deleteTempAttachments,
  deleteWorkPerformed,
  downloadZipFile,
  requestAttachments,
  fetch,
  fetchAll,
  fetchAmendmentHistory,
  fetchCorrectionComments,
  fetchClarificationComments,
  fetchNextToReview,
  fetchPreviewData,
  fetchSummary,
  getFile,
  getLocWorkPerformedSortOrder,
  getWorkPerformedSortOrder,
  markAsSigned,
  retractSubmission,
  reject,
  save,
  saveTentativeAgreement,
  saveReview,
  search,
  startReview,
  submit,
  tentativeAgreementIsUnique,
  toggleDeleteAttachment,
  updateAdrList,
  uploadAttachment,
  uploadAttachmentV2,
};

export default adrActions;

/**
 * Returns a blank Adr template for new Adr.
 * @param {number} projectId
 * @returns {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportDto}
 */
function buildBlankAdr(projectId) {
  const today = new Date();
  return {
    assistantDailyReportId: 0,
    projectId,
    isCem4501: false,
    isCopy: false, // For front-end use only. Used for showing alert box stating diary is created from existing.
    authorEmployeeNo: "", // Populate when mounting AdrMain
    authorFirstName: "", // Populate when mounting AdrMain
    authorLastName: "", // Populate when mounting AdrMain
    authorFullName: "", // Populate when mounting AdrMain
    authorUserId: 0, // Populate when mounting AdrMain
    reportStartDate: today, // default to today
    reportEndDate: today,
    reportNo: "", // today's reportNo
    reportNoSuffix: "",
    customCategory: "",
    contractorStartShift: "0630", // new Date(today.setHours(6, 30)),
    contractorEndShift: "1500", // new Date(today.setHours(15, 0)),
    engineerStartShift: "0700", // new Date(today.setHours(7, 0)),
    engineerEndShift: "1530", // new Date(today.setHours(15, 30)),
    engineerOT: 0,
    engineerOTReason: "",
    engineerOnLeave: false,
    weatherCondition: 0,
    highTemp: "",
    lowTemp: "",
    weatherComment: "",
    nonWorkingDay: false,
    isDraft: true,
    isSubmitted: false,
    isRejected: false,
    isApproved: false,
    status: "Draft",
    createdOn: today,
    draftSavedOn: null,
    submittedOn: null,
    approvedOn: null,
    rejectedOn: null,
    statusDate: null,
    reviewerUserId: null,
    isSuperseded: false,
    supersededOn: null,
    isAmendment: false,
    amendmentNo: 0,
    amendedAssistantDailyReportId: null,
    originalAssistantDailyReportId: null, // Set by the server
    amendmentAnnotation: "",
    generalComments: [],
    workPerformed: [],
    laborRemarks: [],
    equipmentRemarks: [],
    history: [],
    laborSet: [],
    equipmentSet: [],
    correctionComments: [],
    clarificationComments: [],
    tentativeAgreements: [],
  };
}
